import { useEffect, useMemo, useRef, useState } from 'react';
import Modal from './Modal';
import { projectKey, relativeTime } from '../pages/dashboardGroups';
import './SearchOverlay.css';

// Quick-jump palette over the caller's projects, opened by the rail's 🔍 or
// Cmd/Ctrl+K. It replaced the rail's inline filter box: the rail is for
// navigating, this is for jumping, and /projects is for filtering — one 180px
// input was trying to be all three.
//
// Built on `Modal` rather than a fresh overlay so it inherits the focus trap,
// capture-phase Escape, body-scroll lock and mousedown-on-backdrop close that
// every hand-rolled overlay in this repo re-implemented differently.

const MAX_RESULTS = 20;

/**
 * Rank by where the match falls, not just whether it matched: a project whose
 * NAME starts with the query is almost always the one meant, and matching the
 * description at all is the weakest signal. Exported for its own test — the
 * component itself imports Modal and can be rendered, but the ordering is the
 * part worth pinning.
 */
export function searchProjects(projects, shared, query) {
  const q = query.trim().toLowerCase();
  const all = [
    ...(projects || []).map((p) => ({ project: p, isShared: false })),
    ...(shared || []).map((p) => ({ project: p, isShared: true })),
  ];
  if (!q) return all.slice(0, MAX_RESULTS);

  const scored = [];
  for (const entry of all) {
    const name = (entry.project.name || '').toLowerCase();
    const desc = (entry.project.description || '').toLowerCase();
    let rank;
    if (name.startsWith(q)) rank = 0;
    else if (name.includes(q)) rank = 1;
    else if (desc.includes(q)) rank = 2;
    else continue;
    scored.push({ ...entry, rank });
  }
  // Stable within a rank, so the caller's own updatedAt-desc order survives.
  scored.sort((a, b) => a.rank - b.rank);
  return scored.slice(0, MAX_RESULTS);
}

export default function SearchOverlay({ open, onClose, projects, shared, onOpenProject }) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const results = useMemo(() => searchProjects(projects, shared, query), [projects, shared, query]);

  // Reopening should not resume the last search — the palette is a fresh
  // question every time.
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);

  // Clamp rather than reset: typing narrows the list under a highlight that may
  // now be past the end, and resetting to 0 on every keystroke would fight the
  // arrow keys.
  useEffect(() => {
    setActive((cur) => (cur < results.length ? cur : Math.max(0, results.length - 1)));
  }, [results.length]);

  // Modal focuses the first focusable, which is this input; the extra focus()
  // is for the case where the list re-renders around it.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  function choose(entry) {
    if (!entry) return;
    onClose?.();
    onOpenProject(entry.project);
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((cur) => (results.length ? (cur + 1) % results.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((cur) => (results.length ? (cur - 1 + results.length) % results.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(results[active]);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      overlayClassName="search-overlay-backdrop"
      className="search-overlay"
      labelledBy="search-overlay-input"
    >
      <div className="search-overlay-head">
        <span className="search-overlay-icon" aria-hidden="true">
          🔍
        </span>
        <input
          id="search-overlay-input"
          ref={inputRef}
          type="text"
          className="search-overlay-input"
          placeholder="Search projects"
          aria-label="Search projects"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
        />
        <button className="search-overlay-close" aria-label="Close search" onClick={onClose}>
          ✕
        </button>
      </div>

      {results.length === 0 ? (
        <p className="search-overlay-empty">
          {query.trim() ? `No projects match "${query.trim()}"` : 'No projects yet'}
        </p>
      ) : (
        <ul className="search-overlay-results" role="listbox" aria-label="Project results">
          {results.map((entry, i) => (
            <li key={projectKey(entry.project)}>
              <button
                className={`search-overlay-result${i === active ? ' active' : ''}`}
                role="option"
                aria-selected={i === active}
                // Pointer move, not enter: entering re-highlights when the list
                // scrolls under a stationary cursor, which fights the arrows.
                onMouseMove={() => setActive(i)}
                onClick={() => choose(entry)}
              >
                <span className="search-overlay-result-name">{entry.project.name}</span>
                <span className="search-overlay-result-meta">
                  {entry.isShared
                    ? `Shared by ${entry.project.ownerEmail || 'a teammate'}`
                    : relativeTime(entry.project.updatedAt)}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
